import React from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { formatDate, relativeTime } from '../../lib/format.js';
import type { AuditEntry } from '../../api/types.js';

type AuditRowData = AuditEntry & { actorName?: string };

const ACTION_ICONS: Record<string, string> = {
  'board_policy': '📋',
  'integration': '🔌',
  'dispatch': '🎯',
  'triage': '🧠',
  'tech_skill': '💡',
  'skill': '💡',
  'oncall': '🛡️',
  'membership': '👤',
  'user': '👤',
  'technician': '👷',
  'htsteam': '👤',
  'call': '📞',
  'sms': '💬',
  'voice_note': '🎙️',
  'copilot': '🤖',
  'ticket': '🎫',
};

function getIcon(action: string): string {
  for (const [key, icon] of Object.entries(ACTION_ICONS)) {
    if (action.startsWith(key)) return icon;
  }
  return '📝';
}

// Values are action prefixes as written by the API/worker (comma = any of).
const ACTION_GROUPS = [
  { value: '', label: 'All' },
  { value: 'board_policy', label: 'Board Policies' },
  { value: 'integration', label: 'Integrations' },
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'triage', label: 'Triage' },
  { value: 'tech_skill,skill', label: 'Skills' },
  { value: 'oncall', label: 'On-Call' },
  { value: 'membership,user,technician,htsteam', label: 'Users' },
  { value: 'ticket,call,sms,voice_note', label: 'Tickets & Calls' },
  { value: 'copilot,delivery_destination', label: 'AI' },
];

export function AuditLogPage() {
  const [search, setSearch] = React.useState('');
  const [actionFilter, setActionFilter] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  // Debounce search
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = new URLSearchParams();
  if (debouncedSearch) params.set('q', debouncedSearch);
  if (actionFilter) params.set('action', actionFilter);

  const { data, loading, error, refetch } = useGet<{ items: AuditRowData[]; total: number; nextCursor?: string }>(
    `/api/v1/audit?${params.toString()}`,
    [debouncedSearch, actionFilter]
  );

  // "Load more" pages (cursor = timestamp of the last row), reset on filter change.
  const [more, setMore] = React.useState<AuditRowData[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>();
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [moreErr, setMoreErr] = React.useState<string | undefined>();
  React.useEffect(() => {
    setMore([]);
    setCursor(data?.nextCursor);
    setMoreErr(undefined);
  }, [data]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreErr(undefined);
    try {
      const p = new URLSearchParams(params);
      p.set('cursor', cursor);
      const r = await fetch(`/api/v1/audit?${p.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const page = (await r.json()) as { items: AuditRowData[]; nextCursor?: string };
      setMore(prev => [...prev, ...page.items]);
      setCursor(page.nextCursor);
    } catch (e) {
      setMoreErr(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  const entries = [...(data?.items ?? []), ...more];

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[hsl(var(--content))]">Audit Log</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
          Immutable record of all configuration changes, AI decisions, and dispatch actions.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--content-subtle))]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search actor, action, entity…"
            className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content))] placeholder:text-[hsl(var(--content-placeholder))] outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent w-60"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1 p-1 rounded-lg bg-[hsl(var(--surface-subtle))] border border-[hsl(var(--border))]" role="group" aria-label="Filter by action">
          {ACTION_GROUPS.map(g => (
            <button
              key={g.value}
              onClick={() => setActionFilter(g.value)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                actionFilter === g.value
                  ? 'bg-[hsl(var(--surface))] text-[hsl(var(--content))] shadow-sm'
                  : 'text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]'
              )}
            >
              {g.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {data?.total !== undefined && (
            <span className="text-xs text-[hsl(var(--content-placeholder))]">
              {entries.length < data.total ? `${entries.length} of ${data.total}` : data.total} entries
            </span>
          )}
          <button
            onClick={refetch}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]"
            aria-label="Refresh audit log"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Entries */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
        {loading && !data ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} lines={2} />)}
          </div>
        ) : error ? (
          <ErrorState message={`Failed to load the audit log (${error}). Admin or owner role is required.`} onRetry={refetch} />
        ) : entries.length === 0 ? (
          <EmptyState
            icon="🔍"
            title="No audit entries"
            description={debouncedSearch || actionFilter ? 'Try adjusting your search or filter.' : 'Configuration changes, AI decisions and dispatch actions will appear here.'}
          />
        ) : (
          <>
            <div className="divide-y divide-[hsl(var(--border))]">
              {entries.map(entry => (
                <AuditRow key={entry.id} entry={entry} />
              ))}
            </div>
            {(cursor || moreErr) && (
              <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex items-center justify-center gap-3">
                {moreErr && <span className="text-xs text-red-600 dark:text-red-400">{moreErr}</span>}
                {cursor && (
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditRowData }) {
  const [expanded, setExpanded] = React.useState(false);
  const hasDetails = entry.before !== undefined || entry.after !== undefined;

  return (
    <div className="px-5 py-3.5 hover:bg-[hsl(var(--surface-subtle))] transition-colors">
      <div className="flex items-start gap-3">
        <span className="text-xl mt-0.5 shrink-0" aria-hidden="true">{getIcon(entry.action)}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[hsl(var(--content))]" title={entry.actor}>{entry.actorName ?? entry.actor}</span>
            <span className="text-xs font-mono bg-[hsl(var(--surface-subtle))] border border-[hsl(var(--border))] rounded px-1.5 py-0.5 text-[hsl(var(--content-subtle))]">{entry.action}</span>
            <span className="text-xs text-[hsl(var(--content-subtle))]">on</span>
            <span className="text-xs font-medium text-[hsl(var(--content))]">{entry.entity}</span>
            {entry.entityId && (
              <span className="text-xs text-[hsl(var(--content-placeholder))] font-mono">{entry.entityId}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-[hsl(var(--content-placeholder))]">{formatDate(entry.at)} · {relativeTime(entry.at)}</span>
            {hasDetails && (
              <button
                onClick={() => setExpanded(v => !v)}
                className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
              >
                {expanded ? 'Hide diff' : 'Show diff'}
              </button>
            )}
          </div>
          {expanded && hasDetails && (
            <div className="mt-2 grid grid-cols-2 gap-3">
              {entry.before !== undefined && (
                <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 p-2">
                  <p className="text-[10px] font-semibold text-red-600 dark:text-red-400 mb-1">Before</p>
                  <pre className="text-[10px] text-red-800 dark:text-red-300 overflow-auto max-h-24">{JSON.stringify(entry.before, null, 2)}</pre>
                </div>
              )}
              {entry.after !== undefined && (
                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 p-2">
                  <p className="text-[10px] font-semibold text-green-600 dark:text-green-400 mb-1">After</p>
                  <pre className="text-[10px] text-green-800 dark:text-green-300 overflow-auto max-h-24">{JSON.stringify(entry.after, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
